import React, { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTenant } from '../../lib/tenant-context';
import { cn } from '../../lib/utils';
import { ChefHat, Pizza, GlassWater, LayoutDashboard, LogOut, Store } from 'lucide-react';
const STATIONS = [{
  id: 'cocina',
  name: 'Cocina',
  description: 'Preparación general de cocina',
  icon: ChefHat,
  color: 'text-amber-500',
  bgColor: 'bg-amber-500/10',
  borderColor: 'border-amber-500/20',
  hoverBorderColor: 'hover:border-amber-500/50',
  route: (slug: string) => `/admin/${slug}/kds?station=cocina`,
  roles: ['owner', 'admin', 'manager', 'caja', 'cocina']
}, {
  id: 'pizzeria',
  name: 'Pizzería',
  description: 'Estación de pizzas',
  icon: Pizza,
  color: 'text-red-500',
  bgColor: 'bg-red-500/10',
  borderColor: 'border-red-500/20',
  hoverBorderColor: 'hover:border-red-500/50',
  route: (slug: string) => `/admin/${slug}/kds?station=pizzeria`,
  roles: ['owner', 'admin', 'manager', 'caja', 'pizzeria']
}, {
  id: 'bebidas',
  name: 'Batidos',
  description: 'Bebidas y batidos',
  icon: GlassWater,
  color: 'text-blue-500',
  bgColor: 'bg-blue-500/10',
  borderColor: 'border-blue-500/20',
  hoverBorderColor: 'hover:border-blue-500/50',
  route: (slug: string) => `/admin/${slug}/kds?station=bebidas`,
  roles: ['owner', 'admin', 'manager', 'caja', 'bebidas']
}, {
  id: 'caja',
  name: 'Caja',
  description: 'Vista completa del restaurante',
  icon: LayoutDashboard,
  color: 'text-emerald-500',
  bgColor: 'bg-emerald-500/10',
  borderColor: 'border-emerald-500/20',
  hoverBorderColor: 'hover:border-emerald-500/50',
  route: (slug: string) => `/admin/${slug}/dashboard`,
  roles: ['owner', 'admin', 'manager', 'caja']
}];
export function StationSelectPage() {
  const navigate = useNavigate();
  const {
    restaurant,
    role,
    signOut
  } = useTenant();
  // Filter stations based on user role
  const availableStations = STATIONS.filter(station => station.roles.includes(role));
  // Auto-redirect if the user only has access to one station
  useEffect(() => {
    if (availableStations.length === 1) {
      navigate(availableStations[0].route(restaurant.slug), {
        replace: true
      });
    }
  }, [availableStations.length, navigate, restaurant.slug]);
  // If auto-redirecting, show a minimal loading state
  if (availableStations.length === 1) {
    return <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center" data-id="element-1072">
        <div className="w-12 h-12 border-4 border-white/10 border-t-white/50 rounded-full animate-spin" data-id="element-1073" />
      </div>;
  }
  return <div className="min-h-screen bg-[#0B0B0B] flex flex-col items-center justify-center p-4 sm:p-6 font-sans" data-id="element-1074">
      <div className="w-full max-w-2xl mx-auto flex flex-col items-center" data-id="element-1075">
        {/* Header */}
        <div className="flex flex-col items-center text-center mb-10" data-id="element-1076">
          {restaurant.logo_url ? <img src={restaurant.logo_url} alt={restaurant.name} className="w-20 h-20 rounded-2xl object-cover border border-white/10 shadow-xl mb-4 bg-[#141414]" data-id="element-1077" /> : <div className="w-20 h-20 rounded-2xl bg-[#141414] border border-white/10 shadow-xl flex items-center justify-center mb-4" data-id="element-1078">
              <Store className="w-10 h-10 text-white/50" data-id="element-1079" />
            </div>}

          <div className="flex items-center gap-2 mb-2" data-id="element-1080">
            <h2 className="text-lg font-bold text-white/80" data-id="element-1081">
              {restaurant.name}
            </h2>
            <span className="bg-white/10 text-white/70 px-2 py-0.5 rounded-md text-xs font-bold uppercase tracking-wider" data-id="element-1082">
              {role}
            </span>
          </div>

          <h1 className="text-3xl sm:text-4xl font-black text-white tracking-tight mb-2" data-id="element-1083">
            ¿A qué estación querés ingresar?
          </h1>
          <p className="text-base text-white/50" data-id="element-1084">
            Elegí el área de trabajo para continuar
          </p>
        </div>

        {/* Stations Grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 w-full mb-12" data-id="element-1085">
          {availableStations.map(station => {
          const Icon = station.icon;
          return <button key={station.id} onClick={() => navigate(station.route(restaurant.slug))} className={cn('flex flex-col items-center text-center p-6 rounded-2xl bg-[#141414] border transition-all duration-200 cursor-pointer active:scale-[0.98]', station.borderColor, station.hoverBorderColor, 'hover:bg-white/5')} data-id="element-1086">
                <div className={cn('w-16 h-16 rounded-full flex items-center justify-center mb-4', station.bgColor, station.color)} data-id="element-1087">
                  <Icon className="w-8 h-8" data-id="element-1088" />
                </div>
                <h3 className="text-xl font-black text-white mb-1" data-id="element-1089">
                  {station.name}
                </h3>
                <p className="text-sm text-white/50" data-id="element-1090">{station.description}</p>
              </button>;
        })}
        </div>

        {/* Footer / Sign Out */}
        <button onClick={signOut} className="flex items-center gap-2 text-sm font-medium text-white/30 hover:text-white/70 transition-colors px-4 py-2 rounded-lg hover:bg-white/5" data-id="element-1091">
          <LogOut className="w-4 h-4" data-id="element-1092" />
          Cerrar sesión
        </button>
      </div>
    </div>;
}